import { forwardRef, memo, useCallback, useEffect, useState } from 'react';

import { cva } from 'class-variance-authority';

import Button from '@/components/Button';
import Clear from '@/icons/close.svg';
import Search from '@/icons/search.svg';
import { cn, debounce } from '@/utils';

const inputVariants = cva(
	'relative flex w-full min-w-[200px] items-center gap-2 rounded border border-gray-200 bg-background px-3 text-xs transition-colors placeholder:text-gray-500 focus:border-black focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500',
	{
		variants: {
			size: {
				default: 'h-7 px-3',
				xl: 'h-12 px-3 text-sm lg:h-14 lg:px-4 lg:text-base',
				lg: 'h-10 px-3 py-2.5 text-sm lg:h-12 lg:text-base'
			},
			error: {
				true: 'border-border-red focus:border-border-red'
			}
		},
		defaultVariants: {
			size: 'default'
		}
	}
);
const SearchInput = forwardRef(
	(
		{
			value: propValue = '',
			onChange = () => {},
			className,
			type,
			size,
			error,
			errorMessage,
			noDebounced = false,
			...props
		},
		ref
	) => {
		const [searchText, setSearchText] = useState(propValue || '');

		const [isDirty, setIsDirty] = useState(false);

		const debouncedChangeHandler = useCallback(
			debounce(newText => {
				onChange({ target: { value: newText } });
			}, 300),
			[]
		);

		useEffect(() => {
			setIsDirty(Boolean(searchText));
		}, [searchText]);

		const handleChange = e => {
			e.preventDefault();
			setSearchText(e.target.value);
			if (noDebounced) {
				onChange(e);
			} else {
				debouncedChangeHandler(e.target.value);
			}
		};

		const handleClear = e => {
			e.preventDefault();
			setSearchText('');
			onChange({ target: { value: '' }, clear: true });
		};

		return (
			<div
				className={cn(
					inputVariants({
						size,
						className,
						error
					})
				)}
			>
				<Search
					className="pointer-events-none relative z-10 h-4 w-4 flex-shrink-0 text-black"
					width="16"
					height="16"
				/>
				<input
					value={searchText}
					type={type}
					onChange={event => handleChange(event)}
					className="absolute inset-0 h-full w-full flex-shrink bg-transparent pl-9 pr-6 focus-visible:outline-none"
					ref={ref}
					{...props}
				/>
				<Button
					size="iconXs"
					variant="outline"
					className={cn(
						'absolute inset-y-0 right-2 my-auto border-0 p-0',
						!isDirty && 'hidden'
					)}
					onClick={event => handleClear(event)}
				>
					<Clear className="size-4" />
				</Button>
			</div>
		);
	}
);

export default memo(SearchInput);
